// src/admin/AcademicYearsManager.js
import React, { useState, useEffect } from 'react';
import { firestore } from '../firebaseConfig';
import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  writeBatch,
  query,
  orderBy
} from 'firebase/firestore';
import * as XLSX from 'xlsx';
import './AcademicYearsManager.css';

// Hàm chuẩn hóa ID không dấu tiếng Việt
const cleanVietnameseId = (str) => {
  if (!str) return '';
  let slug = str.toString().toLowerCase();

  slug = slug.replace(/đ/g, 'd');
  slug = slug.replace(/[áàảãạăắằẳẵặâấầẩẫậ]/g, 'a');
  slug = slug.replace(/[éèẻẽẹêếềểễệ]/g, 'e');
  slug = slug.replace(/[íìỉĩị]/g, 'i');
  slug = slug.replace(/[óòỏõọôốồổỗộơớờởỡợ]/g, 'o');
  slug = slug.replace(/[úùủũụưứừửữự]/g, 'u');
  slug = slug.replace(/[ýỳỷỹỵ]/g, 'y');

  slug = slug.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  slug = slug.replace(/[^a-z0-9]/g, '-');
  slug = slug.replace(/-+/g, '-');
  slug = slug.replace(/^-+|-+$/g, '');

  return slug;
};

const AcademicYearsManager = () => {
  const [years, setYears] = useState([]);
  const [selectedYearId, setSelectedYearId] = useState('');
  const [grades, setGrades] = useState([]);
  const [classes, setClasses] = useState([]);

  // State UI
  const [activeSubTab, setActiveSubTab] = useState('structure');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', content: '' });

  // Form tạo niên khóa mới
  const [newYear, setNewYear] = useState({
    id: '',
    name: '',
    startDate: '',
    endDate: '',
    cloneFrom: ''
  });

  // Form sửa niên khóa
  const [editingYear, setEditingYear] = useState({
    id: '',
    name: '',
    startDate: '',
    endDate: ''
  });

  // Form khối mới
  const [newGroupData, setNewGroupData] = useState({
    groupName: '',
    firstGradeName: ''
  });

  // Modal States
  const [showAddYearModal, setShowAddYearModal] = useState(false);
  const [showEditYearModal, setShowEditYearModal] = useState(false);
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);

  useEffect(() => {
    fetchYears();
  }, []);

  useEffect(() => {
    if (selectedYearId) {
      fetchYearDetails(selectedYearId);
    }
  }, [selectedYearId]);

  const showFeedback = (type, content) => {
    setMessage({ type, content });
    setTimeout(() => setMessage({ type: '', content: '' }), 4000);
  };

  // --- 1. FIRESTORE QUẢN LÝ NIÊN KHÓA ---
  const fetchYears = async () => {
    setLoading(true);
    try {
      const q = query(collection(firestore, 'academic_years'), orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      const yearsList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setYears(yearsList);

      const current = yearsList.find(y => y.isCurrent);
      if (current && !selectedYearId) {
        setSelectedYearId(current.id);
      } else if (yearsList.length > 0 && !selectedYearId) {
        setSelectedYearId(yearsList[0].id);
      }
    } catch (error) {
      console.error(error);
      showFeedback('error', 'Lỗi khi tải danh sách niên khóa.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateYear = async (e) => {
    e.preventDefault();
    const cleanedYearId = cleanVietnameseId(newYear.id);
    if (!cleanedYearId || !newYear.name) {
      showFeedback('error', 'Vui lòng điền đầy đủ Mã ID và Tên niên khóa.');
      return;
    }

    setLoading(true);
    try {
      const yearRef = doc(firestore, 'academic_years', cleanedYearId);
      await setDoc(yearRef, {
        id: cleanedYearId,
        name: newYear.name,
        startDate: newYear.startDate || '',
        endDate: newYear.endDate || '',
        isActive: true,
        isCurrent: false
      });

      if (newYear.cloneFrom) {
        const batch = writeBatch(firestore);

        const gradesSnap = await getDocs(collection(firestore, `academic_years/${newYear.cloneFrom}/grades`));
        gradesSnap.docs.forEach(gradeDoc => {
          const newGradeRef = doc(firestore, `academic_years/${cleanedYearId}/grades`, gradeDoc.id);
          batch.set(newGradeRef, gradeDoc.data());
        });

        const classesSnap = await getDocs(collection(firestore, `academic_years/${newYear.cloneFrom}/classes`));
        classesSnap.docs.forEach(classDoc => {
          const newClassRef = doc(firestore, `academic_years/${cleanedYearId}/classes`, classDoc.id);
          batch.set(newClassRef, classDoc.data());
        });

        await batch.commit();
      }

      showFeedback('success', `Đã tạo thành công niên khóa ${newYear.name}!`);
      setShowAddYearModal(false);
      setNewYear({ id: '', name: '', startDate: '', endDate: '', cloneFrom: '' });
      setSelectedYearId(cleanedYearId);
      fetchYears();
    } catch (error) {
      console.error(error);
      showFeedback('error', 'Lỗi khi tạo niên khóa mới.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetCurrentYear = async (yearId) => {
    const targetName = years.find(y => y.id === yearId)?.name || yearId;
    if (!window.confirm(`Bạn có chắc chắn muốn đặt Niên khóa "${targetName}" làm NIÊN KHÓA HIỆN TẠI? Các niên khóa khác sẽ chuyển thành không hiện tại.`)) {
      return;
    }

    setLoading(true);
    try {
      const batch = writeBatch(firestore);
      years.forEach(y => {
        const ref = doc(firestore, 'academic_years', y.id);
        batch.update(ref, { isCurrent: y.id === yearId });
      });

      await batch.commit();
      showFeedback('success', 'Đã cập nhật Niên khóa hiện tại của hệ thống!');
      fetchYears();
    } catch (error) {
      console.error(error);
      showFeedback('error', 'Không thể thiết lập niên khóa hiện tại.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActiveYear = async (yearId, currentStatus) => {
    try {
      const ref = doc(firestore, 'academic_years', yearId);
      await updateDoc(ref, { isActive: !currentStatus });
      showFeedback('success', `Đã ${!currentStatus ? 'HIỂN THỊ' : 'ẨN'} niên khóa.`);
      fetchYears();
    } catch (error) {
      console.error(error);
      showFeedback('error', 'Lỗi cập nhật trạng thái hiển thị.');
    }
  };

  const handleOpenEditYear = (yearObj) => {
    setEditingYear({
      id: yearObj.id,
      name: yearObj.name || '',
      startDate: yearObj.startDate || '',
      endDate: yearObj.endDate || ''
    });
    setShowEditYearModal(true);
  };

  const handleSaveEditYear = async (e) => {
    e.preventDefault();
    if (!editingYear.name.trim()) {
      showFeedback('error', 'Tên niên khóa không được để trống.');
      return;
    }

    setLoading(true);
    try {
      const ref = doc(firestore, 'academic_years', editingYear.id);
      await updateDoc(ref, {
        name: editingYear.name.trim(),
        startDate: editingYear.startDate || '',
        endDate: editingYear.endDate || ''
      });

      showFeedback('success', 'Cập nhật thông tin niên khóa thành công!');
      setShowEditYearModal(false);
      fetchYears();
    } catch (error) {
      console.error(error);
      showFeedback('error', 'Không thể sửa thông tin niên khóa.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteYear = async (yearId) => {
    const yearObj = years.find(y => y.id === yearId);
    if (yearObj?.isCurrent) {
      alert('Không thể xóa Niên khóa đang là Niên khóa Hiện tại!');
      return;
    }

    if (!window.confirm(`Xóa niên khóa "${yearObj?.name}" (${yearId})?`)) {
      return;
    }

    setLoading(true);
    try {
      await deleteDoc(doc(firestore, 'academic_years', yearId));
      showFeedback('success', 'Đã xóa niên khóa thành công.');
      if (selectedYearId === yearId) setSelectedYearId('');
      fetchYears();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // --- 2. FIRESTORE QUẢN LÝ CHI TIẾT KHỐI & LỚP ---
  const fetchYearDetails = async (yearId) => {
    try {
      const gradesSnap = await getDocs(collection(firestore, `academic_years/${yearId}/grades`));
      const gradesList = gradesSnap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      setGrades(gradesList);

      const classesSnap = await getDocs(collection(firestore, `academic_years/${yearId}/classes`));
      const classesList = classesSnap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      setClasses(classesList);
    } catch (error) {
      console.error(error);
    }
  };

  const groupedGrades = React.useMemo(() => {
    const groups = {};
    grades.forEach(g => {
      const groupName = g.group || 'Khác';
      if (!groups[groupName]) groups[groupName] = [];
      groups[groupName].push(g);
    });
    return groups;
  }, [grades]);

  const handleAddGrade = async (groupName, gradeName) => {
    if (!gradeName.trim()) return;
    const gradeId = cleanVietnameseId(gradeName);
    const nextOrder = grades.length > 0 ? Math.max(...grades.map(g => g.sortOrder || 0)) + 1 : 1;

    try {
      const ref = doc(firestore, `academic_years/${selectedYearId}/grades`, gradeId);
      await setDoc(ref, {
        id: gradeId,
        name: gradeName,
        group: groupName,
        sortOrder: nextOrder
      });
      showFeedback('success', `Đã thêm cấp lớp ${gradeName}`);
      fetchYearDetails(selectedYearId);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    const { groupName, firstGradeName } = newGroupData;
    if (!groupName.trim() || !firstGradeName.trim()) {
      showFeedback('error', 'Vui lòng điền đầy đủ thông tin.');
      return;
    }

    setLoading(true);
    try {
      const gradeId = cleanVietnameseId(firstGradeName);
      const nextOrder = grades.length > 0 ? Math.max(...grades.map(g => g.sortOrder || 0)) + 1 : 1;

      const ref = doc(firestore, `academic_years/${selectedYearId}/grades`, gradeId);
      await setDoc(ref, {
        id: gradeId,
        name: firstGradeName,
        group: groupName,
        sortOrder: nextOrder
      });

      showFeedback('success', `Đã khởi tạo khối "${groupName}"!`);
      setShowAddGroupModal(false);
      setNewGroupData({ groupName: '', firstGradeName: '' });
      fetchYearDetails(selectedYearId);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateGrade = async (gradeId, newFields) => {
    try {
      const ref = doc(firestore, `academic_years/${selectedYearId}/grades`, gradeId);
      await updateDoc(ref, newFields);
      fetchYearDetails(selectedYearId);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteGrade = async (gradeId) => {
    if (!window.confirm('Xóa cấp lớp này? Các lớp thuộc cấp cũng sẽ bị xóa.')) return;
    setLoading(true);
    try {
      const batch = writeBatch(firestore);
      batch.delete(doc(firestore, `academic_years/${selectedYearId}/grades`, gradeId));
      classes.filter(c => c.gradeId === gradeId).forEach(c => {
        batch.delete(doc(firestore, `academic_years/${selectedYearId}/classes`, c.id));
      });

      const remainingGrades = grades
        .filter(g => g.id !== gradeId)
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

      remainingGrades.forEach((g, idx) => {
        batch.update(doc(firestore, `academic_years/${selectedYearId}/grades`, g.id), { sortOrder: idx + 1 });
      });

      await batch.commit();
      fetchYearDetails(selectedYearId);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculateSortOrders = async () => {
    if (grades.length === 0) return;
    setLoading(true);
    try {
      const batch = writeBatch(firestore);
      const sortedGrades = [...grades].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      sortedGrades.forEach((g, idx) => {
        batch.update(doc(firestore, `academic_years/${selectedYearId}/grades`, g.id), { sortOrder: idx + 1 });
      });
      await batch.commit();
      fetchYearDetails(selectedYearId);
      showFeedback('success', 'Đã tính lại thứ tự cấp lớp.');
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleMoveGradeOrder = async (index, direction) => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === grades.length - 1) return;

    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    const listCopy = [...grades];

    const temp = listCopy[index].sortOrder;
    listCopy[index].sortOrder = listCopy[targetIndex].sortOrder;
    listCopy[targetIndex].sortOrder = temp;

    try {
      const batch = writeBatch(firestore);
      batch.update(doc(firestore, `academic_years/${selectedYearId}/grades`, listCopy[index].id), { sortOrder: listCopy[index].sortOrder });
      batch.update(doc(firestore, `academic_years/${selectedYearId}/grades`, listCopy[targetIndex].id), { sortOrder: listCopy[targetIndex].sortOrder });
      await batch.commit();
      fetchYearDetails(selectedYearId);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAddClass = async (gradeId, className, group) => {
    if (!className.trim()) return;
    const classId = cleanVietnameseId(className);
    const nextOrder = classes.length > 0 ? Math.max(...classes.map(c => c.sortOrder || 0)) + 1 : 1;

    try {
      const ref = doc(firestore, `academic_years/${selectedYearId}/classes`, classId);
      await setDoc(ref, {
        id: classId,
        name: className,
        gradeId: gradeId,
        group: group,
        sortOrder: nextOrder,
        studentCountLimit: 40
      });
      fetchYearDetails(selectedYearId);
    } catch (error) {
      console.error(error);
    }
  };

  const handleUpdateClass = async (classId, fields) => {
    try {
      const ref = doc(firestore, `academic_years/${selectedYearId}/classes`, classId);
      await updateDoc(ref, fields);
      fetchYearDetails(selectedYearId);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteClass = async (classId) => {
    if (!window.confirm('Xóa lớp học này?')) return;
    try {
      await deleteDoc(doc(firestore, `academic_years/${selectedYearId}/classes`, classId));
      fetchYearDetails(selectedYearId);
    } catch (error) {
      console.error(error);
    }
  };

  // --- 3. XUẤT FILE EXCEL CẤU TRÚC KHỐI VÀ LỚP ---
  const handleExportBlockStructure = () => {
    if (grades.length === 0) {
      alert('Chưa có dữ liệu cấp lớp để tải về.');
      return;
    }

    const sortedGrades = [...grades].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
    const data = [
      ['STT', 'Tên Khối', 'Cấp Lớp', 'Mã Cấp Lớp (ID)', 'Thứ Tự Sắp Xếp']
    ];

    sortedGrades.forEach((g, idx) => {
      data.push([
        idx + 1,
        g.group || 'Khác',
        g.name || '',
        g.id || '',
        g.sortOrder ?? ''
      ]);
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'CauTrucKhoi');
    XLSX.writeFile(wb, `CauTrucKhoi_${selectedYearId}.xlsx`);
  };

  const handleExportClassStructure = () => {
    if (classes.length === 0) {
      alert('Chưa có dữ liệu lớp học để tải về.');
      return;
    }

    const gradeOrderMap = {};
    const gradeNameMap = {};
    grades.forEach(g => {
      gradeOrderMap[g.id] = g.sortOrder || 0;
      gradeNameMap[g.id] = g.name || g.id;
    });

    const sortedClasses = [...classes].sort((a, b) => {
      const orderA = gradeOrderMap[a.gradeId] ?? 999;
      const orderB = gradeOrderMap[b.gradeId] ?? 999;
      if (orderA !== orderB) return orderA - orderB;
      return (a.sortOrder || 0) - (b.sortOrder || 0);
    });

    const data = [
      ['STT', 'Khối', 'Cấp Lớp', 'Tên Lớp', 'Mã Lớp (ID)', 'Sĩ Số Tối Đa']
    ];

    sortedClasses.forEach((c, idx) => {
      data.push([
        idx + 1,
        c.group || '',
        gradeNameMap[c.gradeId] || c.gradeId || '',
        c.name || '',
        c.id || '',
        c.studentCountLimit || 40
      ]);
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'CauTrucLop');
    XLSX.writeFile(wb, `CauTrucLop_${selectedYearId}.xlsx`);
  };

  const handleRecalculateClassSortOrders = async () => {
    if (classes.length === 0) return;
    setLoading(true);
    try {
      const gradeOrderMap = {};
      grades.forEach(g => {
        gradeOrderMap[g.id] = g.sortOrder || 0;
      });

      // Sắp xếp lớp: Theo thứ tự cấp trước, sau đó theo tên lớp A-Z
      const sortedClasses = [...classes].sort((a, b) => {
        const orderA = gradeOrderMap[a.gradeId] ?? 999;
        const orderB = gradeOrderMap[b.gradeId] ?? 999;
        if (orderA !== orderB) return orderA - orderB;
        return (a.name || '').localeCompare(b.name || '', 'vi');
      });

      const batch = writeBatch(firestore);
      sortedClasses.forEach((cls, idx) => {
        const ref = doc(firestore, `academic_years/${selectedYearId}/classes`, cls.id);
        batch.update(ref, { sortOrder: idx + 1 });
      });

      await batch.commit();
      fetchYearDetails(selectedYearId);
      showFeedback('success', 'Đã chuẩn hóa và tính lại thứ tự tất cả các lớp học!');
    } catch (error) {
      console.error(error);
      showFeedback('error', 'Lỗi khi tính lại thứ tự lớp.');
    } finally {
      setLoading(false);
    }
  };

  const activeYearObj = years.find(y => y.id === selectedYearId);

  return (
    <div className="academic-years-manager">
      {/* Header chính */}
      <div className="aym-header">
        <div className="aym-header-info">
          <h1>Quản Lý Niên Khóa & Cấu Trúc Lớp</h1>
          <p>Lưu trữ và quản lý phân hệ niên khóa, cấp lớp, danh sách lớp trên Cloud Firestore.</p>
        </div>
        <button
          onClick={() => setShowAddYearModal(true)}
          className="aym-btn aym-btn-primary"
        >
          + Tạo Niên Khóa Mới
        </button>
      </div>

      {message.content && (
        <div className={`aym-alert ${message.type === 'success' ? 'aym-alert-success' : 'aym-alert-error'}`}>
          {message.content}
        </div>
      )}

      <div className="aym-grid">
        {/* CỘT TRÁI: DANH SÁCH NIÊN KHÓA */}
        <div className="aym-sidebar">
          <h2 className="aym-sidebar-title">Niên Khóa Hệ Thống</h2>
          {loading && years.length === 0 && <div className="aym-empty-state">Đang tải danh sách...</div>}

          <div className="aym-years-list">
            {years.map((year) => (
              <div
                key={year.id}
                onClick={() => setSelectedYearId(year.id)}
                className={`aym-year-item ${selectedYearId === year.id ? 'active' : ''}`}
              >
                <div className="aym-year-card-top">
                  <h3 className="aym-year-title">{year.name}</h3>
                  <div className="aym-year-badges">
                    {year.isCurrent && (
                      <span className="aym-badge-current" title="Niên khóa hiện tại của hệ thống">
                        ⭐ Hiện tại
                      </span>
                    )}
                    {!year.isActive && (
                      <span className="aym-badge-hidden" title="Đang bị ẩn với người dùng">
                        🙈 Đang ẩn
                      </span>
                    )}
                  </div>
                </div>

                <div className="aym-year-card-body">
                  <p className="aym-year-id">Mã ID: <span>{year.id}</span></p>
                  <p className="aym-year-dates">
                    📅 {year.startDate || 'N/A'} đến {year.endDate || 'N/A'}
                  </p>
                </div>

                <div className="aym-year-card-actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    title={year.isCurrent ? 'Đây là niên khóa hiện tại' : 'Đặt làm niên khóa hiện tại'}
                    onClick={() => !year.isCurrent && handleSetCurrentYear(year.id)}
                    className={`aym-action-pill ${year.isCurrent ? 'is-current' : 'is-not-current'}`}
                  >
                    ⭐ {year.isCurrent ? 'Hiện tại' : 'Đặt hiện tại'}
                  </button>

                  <button
                    title={year.isActive ? 'Bấm để ẨN niên khóa' : 'Bấm để HIỂN THỊ niên khóa'}
                    onClick={() => handleToggleActiveYear(year.id, year.isActive)}
                    className={`aym-action-pill ${year.isActive ? 'is-active' : 'is-inactive'}`}
                  >
                    {year.isActive ? '👁️ Hiển thị' : '🙈 Đang ẩn'}
                  </button>

                  <button
                    title="Sửa tên và thông tin"
                    onClick={() => handleOpenEditYear(year)}
                    className="aym-action-pill aym-btn-edit"
                  >
                    ✏️ Sửa
                  </button>

                  <button
                    title="Xóa niên khóa"
                    onClick={() => handleDeleteYear(year.id)}
                    className="aym-action-pill aym-btn-delete"
                  >
                    🗑️ Xóa
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CỘT PHẢI: QUẢN LÝ CẤU TRÚC CHI TIẾT */}
        <div className="aym-main-content">
          {!selectedYearId ? (
            <div className="aym-empty-state">Vui lòng chọn hoặc tạo niên khóa mới để bắt đầu cấu hình.</div>
          ) : (
            <div>
              <div className="aym-main-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="aym-badge">
                    Đang cấu hình: {activeYearObj?.name}
                  </span>
                  <button
                    onClick={() => activeYearObj && handleOpenEditYear(activeYearObj)}
                    className="aym-btn-sm"
                    style={{ fontSize: '12px', padding: '2px 8px' }}
                  >
                    ✏️ Sửa Tên
                  </button>
                </div>

                <div className="aym-tabs">
                  <button
                    onClick={() => setActiveSubTab('structure')}
                    className={`aym-tab-btn ${activeSubTab === 'structure' ? 'active' : ''}`}
                  >
                    Cấu Trúc Khối
                  </button>
                  <button
                    onClick={() => setActiveSubTab('classes')}
                    className={`aym-tab-btn ${activeSubTab === 'classes' ? 'active' : ''}`}
                  >
                    Danh Sách Lớp
                  </button>
                </div>
              </div>

              {/* SUBTAB 1: CẤU TRÚC KHỐI & CẤP LỚP */}
              {activeSubTab === 'structure' && (
                <div className="aym-tab-content">
                  <div className="aym-content-actions">
                    <button
                      onClick={handleExportBlockStructure}
                      className="aym-btn aym-btn-secondary"
                      title="Tải về file Excel cấu trúc khối và cấp lớp"
                    >
                      📥 Tải Về Cấu Trúc Khối
                    </button>
                    <button
                      onClick={handleRecalculateSortOrders}
                      className="aym-btn aym-btn-secondary"
                      title="Tính toán và đồng bộ lại thứ tự cấp lớp"
                    >
                      🔄 Tính Lại Thứ Tự Cấp Lớp
                    </button>
                    <button
                      onClick={() => setShowAddGroupModal(true)}
                      className="aym-btn aym-btn-primary"
                    >
                      + Thêm Khối Mới
                    </button>
                  </div>

                  {Object.keys(groupedGrades).map((groupName, groupIdx) => (
                    <div key={groupName} className="aym-block-card">
                      <div className="aym-block-header">
                        <h3 className="aym-block-title">
                          <span className="aym-block-number">{groupIdx + 1}</span>
                          {groupName}
                        </h3>
                        <div className="aym-block-header-actions">
                          <button
                            onClick={() => {
                              const newName = prompt('Nhập tên mới cho khối này:', groupName);
                              if (newName && newName !== groupName) {
                                const batch = writeBatch(firestore);
                                groupedGrades[groupName].forEach(g => {
                                  batch.update(doc(firestore, `academic_years/${selectedYearId}/grades`, g.id), { group: newName });
                                });
                                batch.commit().then(() => fetchYearDetails(selectedYearId));
                              }
                            }}
                            className="aym-action-btn aym-edit-btn"
                            title="Đổi tên khối"
                          >
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                          </button>
                        </div>
                      </div>

                      <div className="aym-grade-list">
                        {groupedGrades[groupName].map((g) => (
                          <div key={g.id} className="aym-grade-item">
                            <div className="aym-grade-info">
                              <span className="aym-grade-id">ID: {g.id}</span>
                              <span className="aym-grade-order-badge">Thứ tự: {g.sortOrder}</span>
                              <span className="aym-grade-name">{g.name}</span>
                            </div>

                            <div className="aym-grade-actions">
                              <button
                                onClick={() => handleMoveGradeOrder(grades.findIndex(item => item.id === g.id), 'up')}
                                className="aym-arrow-btn"
                                title="Đẩy lên"
                              >
                                ▲
                              </button>
                              <button
                                onClick={() => handleMoveGradeOrder(grades.findIndex(item => item.id === g.id), 'down')}
                                className="aym-arrow-btn"
                                title="Hạ xuống"
                              >
                                ▼
                              </button>

                              <button
                                onClick={() => {
                                  const name = prompt('Nhập tên mới cho cấp lớp:', g.name);
                                  if (name) handleUpdateGrade(g.id, { name });
                                }}
                                className="aym-action-btn aym-edit-btn"
                                title="Sửa tên"
                              >
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                              </button>
                              <button
                                onClick={() => handleDeleteGrade(g.id)}
                                className="aym-action-btn aym-delete-btn"
                                title="Xóa cấp"
                              >
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="aym-block-footer">
                        <button
                          onClick={() => {
                            const name = prompt(`Nhập tên cấp lớp mới thuộc khối ${groupName}:`);
                            if (name) handleAddGrade(groupName, name);
                          }}
                          className="aym-link-btn"
                        >
                          + Thêm Cấp Lớp Mới
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* SUBTAB 2: DANH SÁCH LỚP HỌC */}
              {activeSubTab === 'classes' && (
                <div className="aym-tab-content">
                  <div className="aym-content-actions">
                    <button
                      onClick={handleRecalculateClassSortOrders}
                      className="aym-btn aym-btn-secondary"
                      title="Tự động sắp xếp chuẩn theo Cấp lớp và tên lớp (A -> B -> C)"
                    >
                      🔄 Tính Lại Thứ Tự Lớp
                    </button>
                    <button
                      onClick={handleExportClassStructure}
                      className="aym-btn aym-btn-secondary"
                      title="Tải về file Excel danh sách và cấu trúc lớp học"
                    >
                      📥 Tải Về Cấu Trúc Lớp
                    </button>
                  </div>

                  {grades.map((grade) => {
                    const gradeClasses = classes.filter(c => c.gradeId === grade.id);
                    return (
                      <div key={grade.id} className="aym-classes-section">
                        <div className="aym-classes-section-header">
                          <div>
                            <h4>Cấp: {grade.name} (Thứ tự: {grade.sortOrder})</h4>
                            <p>Khối: {grade.group} • Số lớp hiện tại: <span>{gradeClasses.length}</span></p>
                          </div>
                          <button
                            onClick={() => {
                              const name = prompt(`Nhập tên lớp mới cho cấp ${grade.name} (Ví dụ: ${grade.name} A Nữ, ${grade.name} B Nam):`);
                              if (name) handleAddClass(grade.id, name, grade.group);
                            }}
                            className="aym-link-btn"
                          >
                            + Thêm Lớp
                          </button>
                        </div>

                        {gradeClasses.length === 0 ? (
                          <div className="aym-empty-state">Chưa có lớp nào được đăng ký trong cấp này.</div>
                        ) : (
                          <div className="aym-classes-grid">
                            {gradeClasses.map((c) => (
                              <div key={c.id} className="aym-class-card">
                                <div className="aym-class-info">
                                  <h5 className="aym-class-name">{c.name}</h5>
                                  <div className="aym-class-meta">
                                    <span className="aym-class-id">ID: {c.id}</span>
                                    <div className="aym-class-limit-container">
                                      <span className="aym-class-limit-label">Tối đa:</span>
                                      <input
                                        type="number"
                                        defaultValue={c.studentCountLimit || 40}
                                        className="aym-class-limit-input"
                                        onBlur={(e) => handleUpdateClass(c.id, { studentCountLimit: parseInt(e.target.value) || 40 })}
                                      />
                                      <span className="aym-class-limit-label">em</span>
                                    </div>
                                  </div>
                                </div>

                                <div className="aym-class-actions">
                                  <button
                                    onClick={() => {
                                      const newName = prompt(`Nhập tên mới cho lớp "${c.name}":`, c.name);
                                      if (newName && newName.trim() && newName.trim() !== c.name) {
                                        handleUpdateClass(c.id, { name: newName.trim() });
                                      }
                                    }}
                                    className="aym-action-btn aym-edit-btn"
                                    title="Sửa tên lớp"
                                  >
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                  </button>
                                  <button
                                    onClick={() => handleDeleteClass(c.id)}
                                    className="aym-action-btn aym-delete-btn"
                                    title="Xóa lớp"
                                  >
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* MODAL THÊM NIÊN KHÓA MỚI */}
      {showAddYearModal && (
        <div className="aym-modal-overlay">
          <div className="aym-modal-card">
            <h3 className="aym-modal-title">Tạo Niên Khóa Mới</h3>
            <form onSubmit={handleCreateYear} className="aym-form">
              <div className="aym-form-group">
                <label className="aym-form-label">Mã ID (VD: 2026-2027)</label>
                <input
                  type="text"
                  value={newYear.id}
                  onChange={(e) => setNewYear({ ...newYear, id: e.target.value })}
                  placeholder="2026-2027"
                  className="aym-form-input"
                  required
                />
              </div>

              <div className="aym-form-group">
                <label className="aym-form-label">Tên Niên Khóa</label>
                <input
                  type="text"
                  value={newYear.name}
                  onChange={(e) => setNewYear({ ...newYear, name: e.target.value })}
                  placeholder="Niên khóa 2026 - 2027"
                  className="aym-form-input"
                  required
                />
              </div>

              <div className="aym-form-grid">
                <div className="aym-form-group">
                  <label className="aym-form-label">Ngày Bắt Đầu</label>
                  <input
                    type="date"
                    value={newYear.startDate}
                    onChange={(e) => setNewYear({ ...newYear, startDate: e.target.value })}
                    className="aym-form-input"
                  />
                </div>
                <div className="aym-form-group">
                  <label className="aym-form-label">Ngày Kết Thúc</label>
                  <input
                    type="date"
                    value={newYear.endDate}
                    onChange={(e) => setNewYear({ ...newYear, endDate: e.target.value })}
                    className="aym-form-input"
                  />
                </div>
              </div>

              <div className="aym-form-group">
                <label className="aym-form-label">Sao Chép Cấu Trúc Từ Niên Khóa Cũ</label>
                <select
                  value={newYear.cloneFrom}
                  onChange={(e) => setNewYear({ ...newYear, cloneFrom: e.target.value })}
                  className="aym-form-select"
                >
                  <option value="">-- Tạo mới hoàn toàn --</option>
                  {years.map(y => (
                    <option key={y.id} value={y.id}>{y.name}</option>
                  ))}
                </select>
                <p className="aym-form-help">Hệ thống sẽ tự động nhân bản toàn bộ danh mục Khối và Cấp lớp cũ sang niên khóa mới.</p>
              </div>

              <div className="aym-modal-actions">
                <button
                  type="button"
                  onClick={() => setShowAddYearModal(false)}
                  className="aym-btn aym-btn-secondary"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  className="aym-btn aym-btn-primary"
                >
                  Lưu Lại
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL SỬA TÊN & THÔNG TIN NIÊN KHÓA */}
      {showEditYearModal && (
        <div className="aym-modal-overlay">
          <div className="aym-modal-card">
            <h3 className="aym-modal-title">Sửa Thông Tin Niên Khóa</h3>
            <form onSubmit={handleSaveEditYear} className="aym-form">
              <div className="aym-form-group">
                <label className="aym-form-label">Mã ID (Không thể sửa)</label>
                <input
                  type="text"
                  value={editingYear.id}
                  disabled
                  className="aym-form-input"
                  style={{ backgroundColor: '#f1f5f9', cursor: 'not-allowed' }}
                />
              </div>

              <div className="aym-form-group">
                <label className="aym-form-label">Tên Niên Khóa Mới</label>
                <input
                  type="text"
                  value={editingYear.name}
                  onChange={(e) => setEditingYear({ ...editingYear, name: e.target.value })}
                  placeholder="Ví dụ: Niên khóa 2026 - 2027"
                  className="aym-form-input"
                  required
                />
              </div>

              <div className="aym-form-grid">
                <div className="aym-form-group">
                  <label className="aym-form-label">Ngày Bắt Đầu</label>
                  <input
                    type="date"
                    value={editingYear.startDate}
                    onChange={(e) => setEditingYear({ ...editingYear, startDate: e.target.value })}
                    className="aym-form-input"
                  />
                </div>
                <div className="aym-form-group">
                  <label className="aym-form-label">Ngày Kết Thúc</label>
                  <input
                    type="date"
                    value={editingYear.endDate}
                    onChange={(e) => setEditingYear({ ...editingYear, endDate: e.target.value })}
                    className="aym-form-input"
                  />
                </div>
              </div>

              <div className="aym-modal-actions">
                <button
                  type="button"
                  onClick={() => setShowEditYearModal(false)}
                  className="aym-btn aym-btn-secondary"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  className="aym-btn aym-btn-primary"
                >
                  Cập Nhật
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL TẠO KHỐI MỚI */}
      {showAddGroupModal && (
        <div className="aym-modal-overlay">
          <div className="aym-modal-card">
            <h3 className="aym-modal-title">Khởi Tạo Khối Mới</h3>
            <form onSubmit={handleCreateGroup} className="aym-form">
              <div className="aym-form-group">
                <label className="aym-form-label">Tên Khối Giáo Lý</label>
                <input
                  type="text"
                  value={newGroupData.groupName}
                  onChange={(e) => setNewGroupData({ ...newGroupData, groupName: e.target.value })}
                  placeholder="Ví dụ: Khối Rước Lễ, Khối Bao Đồng"
                  className="aym-form-input"
                  required
                />
              </div>

              <div className="aym-form-group">
                <label className="aym-form-label">Tên Cấp Lớp Đầu Tiên Của Khối</label>
                <input
                  type="text"
                  value={newGroupData.firstGradeName}
                  onChange={(e) => setNewGroupData({ ...newGroupData, firstGradeName: e.target.value })}
                  placeholder="Ví dụ: Rước Lễ 1, Bao Đồng 1"
                  className="aym-form-input"
                  required
                />
              </div>

              <div className="aym-modal-actions">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddGroupModal(false);
                    setNewGroupData({ groupName: '', firstGradeName: '' });
                  }}
                  className="aym-btn aym-btn-secondary"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  className="aym-btn aym-btn-primary"
                >
                  Khởi Tạo
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AcademicYearsManager;